import { useEffect, useState } from "react";
import { useTranslations } from "use-intl";
import BlockOutlinedIcon from "@mui/icons-material/BlockOutlined";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import PersonSearchOutlinedIcon from "@mui/icons-material/PersonSearchOutlined";
import CardMembershipOutlinedIcon from "@mui/icons-material/CardMembershipOutlined";
import LogoutOutlinedIcon from "@mui/icons-material/LogoutOutlined";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  List,
  ListItemButton,
  ListItemText,
  Skeleton,
  TextField,
  Typography,
} from "@mui/material";

import CasaLogo from "../../logo/CasaLogo";
import { logout } from "../../../keycloak";
import { buildMailto } from "../../../utils/mailto";
import {
  getInternalTenantList,
  switchInternalTenant,
  Tenant,
} from "../../../features/TenantSelection/services/api";
import styles from "./AccessRestriction.module.scss";

export type AccessRestrictionReason =
  | "TENANT_INACTIVE"
  | "BUSINESS_INACTIVE"
  | "NO_TENANT_ACCESS"
  | "NO_SUBSCRIPTION";

interface AccessRestrictionProps {
  reason: AccessRestrictionReason;
  businessName?: string;
  /** TENANT_INACTIVE only — the deactivated tenant's name, shown for both a regular user and an
   * internal user (who additionally gets the TenantPicker below). */
  tenantName?: string;
  /** Whoever hit the restriction, for the prefilled support mailto — not shown in the UI. */
  userEmail?: string;
  /** Offers a tenant picker instead of a dead end — see TenantPicker below. Only meaningful for
   * TENANT_INACTIVE / NO_TENANT_ACCESS; a deactivated business (or one with no active
   * subscription) is not a deadlock — the navbar's business switcher already covers it. */
  isInternalUser?: boolean;
}

const COPY: Record<
  AccessRestrictionReason,
  {
    icon: JSX.Element;
    chip: string;
    key: "tenantInactive" | "businessInactive" | "noTenantAccess" | "noSubscription";
  }
> = {
  TENANT_INACTIVE: {
    icon: <BlockOutlinedIcon className={styles.icon} />,
    chip: "Inactive",
    key: "tenantInactive",
  },
  BUSINESS_INACTIVE: {
    icon: <StorefrontOutlinedIcon className={styles.icon} />,
    chip: "Business Inactive",
    key: "businessInactive",
  },
  NO_TENANT_ACCESS: {
    icon: <PersonSearchOutlinedIcon className={styles.icon} />,
    chip: "No Access",
    key: "noTenantAccess",
  },
  NO_SUBSCRIPTION: {
    icon: <CardMembershipOutlinedIcon className={styles.icon} />,
    chip: "Not Subscribed",
    key: "noSubscription",
  },
};

// Prefilled so support has the basics (who, which tenant/business, roughly when) without a
// back-and-forth — the person still has to click send themselves, nothing fires automatically.
export const buildSupportMailto = (
  chip: string,
  userEmail?: string,
  subjectName?: string,
  subjectLabel?: "Tenant" | "Business",
): string =>
  buildMailto("care@casaretail.ai", `CASA access issue — ${chip}`, [
    `Reason: ${chip}`,
    userEmail && `User: ${userEmail}`,
    subjectName && subjectLabel && `${subjectLabel}: ${subjectName}`,
    `Attempted at: ${new Date().toString()}`,
  ]);

const TenantPicker = () => {
  const t = useTranslations("ui");
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [switching, setSwitching] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const timer = setTimeout(async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await getInternalTenantList({ tenant: search || undefined, limit: 10 });
        setTenants(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : t("accessRestriction.fetchTenantsFailed"));
        setTenants([]);
      } finally {
        setLoading(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [search, t]);

  const handleSelect = async (tenant: Tenant) => {
    if (switching) return;
    setSwitching(true);
    setError(null);
    try {
      await switchInternalTenant(tenant.id);
      window.location.reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : t("accessRestriction.switchTenantFailed"));
      setSwitching(false);
    }
  };

  return (
    <Box className={styles.picker}>
      <Typography variant="subtitle2" className={styles.pickerLabel}>
        {t("accessRestriction.switchTenant")}
      </Typography>
      <TextField
        fullWidth
        size="small"
        placeholder={t("accessRestriction.searchTenant")}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        disabled={switching}
      />
      {error && (
        <Alert severity="error" className={styles.pickerAlert}>
          {error}
        </Alert>
      )}
      {loading ? (
        <Skeleton variant="rectangular" height={120} className={styles.pickerSkeleton} />
      ) : (
        <List className={styles.pickerList}>
          {tenants.length === 0 && (
            <Typography variant="body2" color="text.secondary" className={styles.pickerEmpty}>
              {t("accessRestriction.noTenants")}
            </Typography>
          )}
          {tenants.map((tenant) => (
            <ListItemButton key={tenant.id} disabled={switching} onClick={() => handleSelect(tenant)}>
              <ListItemText
                primary={tenant.esKey}
                secondary={tenant.zohoCustomerName ? `#${tenant.zohoCustomerName}` : t("accessRestriction.tenantId", { id: tenant.id })}
              />
            </ListItemButton>
          ))}
        </List>
      )}
    </Box>
  );
};

const AccessRestriction = ({
  reason,
  businessName,
  tenantName,
  userEmail,
  isInternalUser,
}: AccessRestrictionProps) => {
  const t = useTranslations("ui");
  const copy = COPY[reason];
  const isBusinessLevel = reason === "BUSINESS_INACTIVE" || reason === "NO_SUBSCRIPTION";
  const showPicker = isInternalUser && !isBusinessLevel;
  const subjectName = reason === "TENANT_INACTIVE" ? tenantName : businessName;
  const supportMailto = buildSupportMailto(
    copy.chip,
    userEmail,
    subjectName,
    reason === "TENANT_INACTIVE" ? "Tenant" : isBusinessLevel ? "Business" : undefined,
  );

  return (
    <Box className={styles.container}>
      <Box className={styles.header}>
        <CasaLogo />
      </Box>

      <Box className={styles.body}>
        <Box className={styles.card}>
          <Box className={styles.iconWrapper}>{copy.icon}</Box>

          <Chip
            label={t(`accessRestriction.${copy.key}.chip`)}
            size="small"
            className={styles.chip}
            sx={{ bgcolor: "error.light", color: "error.dark", fontWeight: 400 }}
          />

          <Typography variant="h5" className={styles.title}>
            {t(`accessRestriction.${copy.key}.title`)}
          </Typography>

          <Typography variant="body2" className={styles.subtitle}>
            {subjectName || reason === "NO_TENANT_ACCESS"
              ? t(`accessRestriction.${copy.key}.body`, { name: subjectName ?? "" })
              : t(`accessRestriction.${copy.key}.bodyUnnamed`)}
          </Typography>

          {showPicker && (
            <>
              <Divider className={styles.divider} />
              <TenantPicker />
            </>
          )}

          <Divider className={styles.divider} />

          <Box className={styles.supportBox}>
            <MailOutlineIcon fontSize="small" sx={{ color: "text.secondary" }} />
            <Box>
              <Typography variant="body2" color="text.secondary">
                {t("accessRestriction.needHelp")}
              </Typography>
              <Typography
                component="a"
                href={supportMailto}
                variant="body2"
                fontWeight={600}
                color="text.primary"
                sx={{ textDecoration: "none" }}
              >
                {"care@casaretail.ai"}
              </Typography>
            </Box>
          </Box>

          {!isBusinessLevel && (
            <Button
              variant="outlined"
              color="error"
              size="small"
              startIcon={<LogoutOutlinedIcon />}
              onClick={logout}
              className={styles.logoutButton}
            >
              {t("accessRestriction.signOut")}
            </Button>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default AccessRestriction;
